import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import COUNTRIES from '../../constants/countries';

const getInitialState = (profile) => ({
  displayName: profile.displayName || '',
  email: profile.email || '',
  jobTitle: profile.jobTitle || '',
  bio: profile.bio || '',
  location: profile.location || '',
});

const ProfileSettingsForm = ({ profile, onSave }) => {
  const [formState, setFormState] = useState(() => getInitialState(profile));
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setFormState(getInitialState(profile));
    setErrors({});
    setIsDirty(false);
  }, [profile]);

  const avatarLetter = useMemo(() => {
    const name = formState.displayName || profile.displayName || profile.email;
    return name ? name.charAt(0).toUpperCase() : 'U';
  }, [formState.displayName, profile.displayName, profile.email]);

  const validate = () => {
    const nextErrors = {};

    if (!formState.displayName.trim()) {
      nextErrors.displayName = 'Display name is required.';
    }

    if (!formState.email.trim()) {
      nextErrors.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email.trim())) {
      nextErrors.email = 'Please enter a valid email address.';
    }

    if (formState.bio.length > 280) {
      nextErrors.bio = 'Bio must be 280 characters or fewer.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
    setIsDirty(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSaving(true);
    try {
      await Promise.resolve(onSave({
        ...profile,
        ...formState,
      }));
      setIsDirty(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="account-section">
      <header className="account-section__header">
        <div>
          <h2>Profile Settings</h2>
          <p>Control the information other members and collaborators see about you.</p>
        </div>
        <div className="profile-preview">
          <div className="profile-preview__avatar">{avatarLetter}</div>
          <div>
            <p className="profile-preview__name">{formState.displayName || 'Unnamed user'}</p>
            <p className="profile-preview__role">{formState.jobTitle || 'Role not set'}</p>
          </div>
        </div>
      </header>

      <form className="account-form" onSubmit={handleSubmit} noValidate>
        <div className="form-grid">
          <label className="form-field">
            <span className="form-field__label">Display name</span>
            <input
              type="text"
              name="displayName"
              value={formState.displayName}
              onChange={handleChange}
              placeholder="e.g. Alex Rivera"
            />
            {errors.displayName && <span className="form-field__error">{errors.displayName}</span>}
          </label>

          <label className="form-field">
            <span className="form-field__label">Email address</span>
            <input
              type="email"
              name="email"
              value={formState.email}
              onChange={handleChange}
              placeholder="you@example.com"
            />
            {errors.email && <span className="form-field__error">{errors.email}</span>}
          </label>

          <label className="form-field">
            <span className="form-field__label">Job title</span>
            <input
              type="text"
              name="jobTitle"
              value={formState.jobTitle}
              onChange={handleChange}
              placeholder="e.g. Art Director"
            />
          </label>

          <label className="form-field">
            <span className="form-field__label">Location</span>
            <select
              name="location"
              value={formState.location}
              onChange={handleChange}
            >
              <option value="">Select country</option>
              {COUNTRIES.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="form-field">
          <span className="form-field__label">Short bio</span>
          <textarea
            name="bio"
            value={formState.bio}
            onChange={handleChange}
            rows={4}
            placeholder="Share your expertise or the focus of your creative work."
          />
          <div className="form-field__hint">
            {errors.bio ? (
              <span className="form-field__error">{errors.bio}</span>
            ) : (
              <span>{formState.bio.length}/280 characters</span>
            )}
          </div>
        </label>

        <div className="form-divider" aria-hidden="true" />

        <div className="form-actions">
          <button type="submit" className="primary-btn" disabled={isSaving || !isDirty}>
            {isSaving ? 'Saving…' : 'Save profile'}
          </button>
        </div>
      </form>
    </section>
  );
};

ProfileSettingsForm.propTypes = {
  profile: PropTypes.shape({
    displayName: PropTypes.string,
    email: PropTypes.string,
    jobTitle: PropTypes.string,
    bio: PropTypes.string,
    location: PropTypes.string,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
};

export default ProfileSettingsForm;
